// Date / time formatting helpers shared across components.

import type { RoleLifecycle, RoleModel, RoleView } from "./types";

// Clamp an operator-entered /loop cadence to the server-enforced [30, 3600] range
// (mirrors the LoopIntervalRequest bound) so the live loop-interval control never POSTs
// a value the backend will 422. NaN / non-finite → the 60s main-agent default.
export function clampLoopSeconds(value: number): number {
  if (!Number.isFinite(value)) return 60;
  return Math.min(3600, Math.max(30, Math.round(value)));
}

// Readable plan name for a task/slot `plan_ref`. Strips the "plans/active/" prefix
// and the ".md" suffix so a table cell shows the plan, not the path. Orphan rows can
// carry an empty plan_ref (no YAML entry to read one from) → "—".
export function planBasename(planRef: string | null | undefined): string {
  if (!planRef) return "—";
  return planRef.replace(/^plans\/active\//, "").replace(/\.md$/, "");
}

// Filename for the "Download log" button in the log viewer. The label can contain
// "#", "/", spaces etc. (slot "#7", agent ids) — sanitise to a safe filesystem token,
// falling back to the scope when the label has no usable chars. e.g. ("slot", "#7") →
// "slot-7-log.txt"; ("agent", "vm-0/main") → "agent-vm-0_main-log.txt".
export function logDownloadFilename(scope: string, label: string): string {
  const safe = label.replace(/[^A-Za-z0-9_.-]+/g, "_").replace(/^_+|_+$/g, "") || scope;
  return `${scope}-${safe}-log.txt`;
}

export function fmtAgo(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso).getTime();
  if (isNaN(d)) return "—";
  const secs = Math.floor((Date.now() - d) / 1000);
  if (secs < 5) return "now";
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function fmtClock(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toISOString().slice(11, 19);
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toISOString().slice(0, 19).replace("T", " ") + " UTC";
}

export function fmtShortSha(sha: string | null | undefined): string {
  if (!sha) return "—";
  return sha.slice(0, 7);
}

/** Millisecond delta between two ISO timestamps, or null when either end is
 * missing/unparsable — the shared building block for the backlog detail
 * table's "Queue lag" (queued_at → dispatched_at) and "Duration"
 * (dispatched_at → done_at) columns (2026-07-25). Never clamped here so a
 * caller doing its own math (e.g. a sort comparator) sees the true delta;
 * `fmtDurationBetween` clamps negatives defensively before formatting. */
export function durationBetweenMs(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
): number | null {
  if (!startIso || !endIso) return null;
  const start = new Date(startIso).getTime();
  const end = new Date(endIso).getTime();
  if (isNaN(start) || isNaN(end)) return null;
  return end - start;
}

/** Format a duration SPAN (distinct from `fmtAgo`/`fmtClock`, which format a
 * POINT in time): "45s" / "12m" / "3h 8m" / "2d 4h". Drops the finer unit
 * once it would read as noise (an "h" reading never shows seconds), but
 * keeps it while it's still the dominant magnitude (a bare "3h" throws away
 * real precision an operator reading queue-lag/duration wants). */
export function fmtDuration(ms: number): string {
  const totalSec = Math.floor(Math.max(0, ms) / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const totalMin = Math.floor(totalSec / 60);
  if (totalMin < 60) return `${totalMin}m`;
  const totalHours = Math.floor(totalMin / 60);
  const remMin = totalMin % 60;
  if (totalHours < 24) return remMin > 0 ? `${totalHours}h ${remMin}m` : `${totalHours}h`;
  const days = Math.floor(totalHours / 24);
  const remHours = totalHours % 24;
  return remHours > 0 ? `${days}d ${remHours}h` : `${days}d`;
}

/** "—" when either endpoint timestamp is missing (still-queued → no
 * dispatched_at; still-dispatched → no done_at yet), else the formatted
 * span between them. This is what the table cells render. */
export function fmtDurationBetween(startIso: string | null | undefined, endIso: string | null | undefined): string {
  const ms = durationBetweenMs(startIso, endIso);
  return ms == null ? "—" : fmtDuration(ms);
}

/** Format a future ISO timestamp as "in Xm" / "in Xh Ym" / "expired".
 * Use for rate_limited_until and other deadline fields (unlike fmtAgo which is past-only). */
export function fmtIn(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso).getTime();
  if (isNaN(d)) return "—";
  const secs = Math.floor((d - Date.now()) / 1000);
  if (secs <= 0) return "expired";
  if (secs < 60) return `in ${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `in ${mins}m`;
  const hours = Math.floor(mins / 60);
  return `in ${hours}h ${mins % 60}m`;
}

// ─── role registry helpers (Registry tab) ───────────────────────────────────
// Pure functions backing the read-only RolesPanel so the renderer stays thin and
// the display logic is unit-testable (no DOM).

/** CSS modifier class for a role's model chip — colour-codes opus/sonnet/haiku.
 * Reuses the `model-badge` base + a per-model modifier that styles.css tints. */
export function modelBadgeClass(model: RoleModel): string {
  return `model-badge model-${model}`;
}

// Tier order so opus > sonnet > haiku reads top-to-bottom within equal lifecycle.
const MODEL_RANK: Record<RoleModel, number> = { opus: 0, sonnet: 1, haiku: 2 };
// Persistent agents are the always-on backbone; surface them above one-shots/crons.
const LIFECYCLE_RANK: Record<RoleLifecycle, number> = { persistent: 0, one_shot: 1, scheduled: 2 };

/** Deterministic display order for the registry table: lifecycle (persistent
 * first) → model (opus first) → role name (alpha). The backend already sorts by
 * role, but the dashboard groups by lifecycle/model for scannability. Pure +
 * non-mutating (returns a new array). */
export function sortRolesForDisplay(roles: RoleView[]): RoleView[] {
  return [...roles].sort((a, b) => {
    const lc = LIFECYCLE_RANK[a.lifecycle] - LIFECYCLE_RANK[b.lifecycle];
    if (lc !== 0) return lc;
    const mr = MODEL_RANK[a.model] - MODEL_RANK[b.model];
    if (mr !== 0) return mr;
    return a.role.localeCompare(b.role);
  });
}

export interface RegistrySummary {
  total: number;
  byModel: Record<RoleModel, number>;
  byLifecycle: Record<RoleLifecycle, number>;
  unschematized: number;
}

/** Roll up a role list into counts by model + lifecycle (+ unschematized tally)
 * for the panel header. Pure — drives the "N roles · X opus · …" subtitle. */
export function summarizeRegistry(roles: RoleView[]): RegistrySummary {
  const summary: RegistrySummary = {
    total: roles.length,
    byModel: { opus: 0, sonnet: 0, haiku: 0 },
    byLifecycle: { persistent: 0, one_shot: 0, scheduled: 0 },
    unschematized: 0,
  };
  for (const r of roles) {
    summary.byModel[r.model] += 1;
    summary.byLifecycle[r.lifecycle] += 1;
    if (!r.has_frontmatter) summary.unschematized += 1;
  }
  return summary;
}

/** "184,231 / 1,000,000 tokens" — the arithmetic behind the percentage.
 *
 * Exists because the percentage alone hides the failure mode that has caused EVERY
 * context incident here: a wrong denominator. A sonnet-4.6 worker reading "16%" was
 * actually full (window assumed 1M, really 200K), and DeepSeek workers reading ~95%
 * were at ~23% (window learned as ~240K, really 1,048,565). In both cases the pct
 * looked unremarkable and only the window was wrong, so surfacing the window is what
 * makes the next one catchable by eye (operator request 2026-08-10).
 *
 * Returns "" when either number is missing — a partial "? / 1,000,000" would invite
 * exactly the misreading this is meant to prevent.
 */
export function ctxArithmetic(tokens?: number | null, windowTokens?: number | null): string {
  if (tokens == null || windowTokens == null || windowTokens <= 0) return "";
  return `${tokens.toLocaleString()} / ${windowTokens.toLocaleString()} tokens`;
}

// ─── human-fleet operator identity (ao_human_fleet_integration_2026_08_15) ──

/** The only two registered human operators (config.human_slot_ids(): 9001=Ikenna,
 * 9002=Harsh) — `ao-register.sh <label>` is called with the operator's own lowercase
 * name (confirmed live: agent `label:"ikenna"`, upcoming `label:"harsh"` for slot
 * 9002), NOT the `machine=` value used only for JWT minting. Fixed 2-entry map, not a
 * generic capitalizer — an unrecognized label (future third operator, demo/mock data)
 * falls back to the raw label rather than fabricating a name. */
const HUMAN_OPERATOR_DISPLAY_NAMES: Record<string, string> = {
  ikenna: "Ikenna",
  harsh: "Harsh",
};

/** Per-row display name for a human/planning-human agent or slot — replaces the
 * generic "human"/"human (planning)" agent_kind bucket label (correct at the
 * section/filter level, wrong on an individual row) with WHO is actually there. */
export function humanOperatorDisplayName(label: string): string {
  return HUMAN_OPERATOR_DISPLAY_NAMES[label.toLowerCase()] ?? label;
}

/** Mirrors HUMAN_OPERATOR_DISPLAY_NAMES's fixed 2-entry map (config.human_slot_ids()):
 * ikenna=9001, harsh=9002. Used to make a human-fleet row's SCOPE explicit
 * (ao_fleet_regression_triad_2026_08_16 Finding 3) — a bare operator name reads as
 * the operator's overall presence, when it is really just this ONE opt-in
 * self-registration; unrecognized labels return null rather than guessing. */
const HUMAN_OPERATOR_SLOT_IDS: Record<string, number> = {
  ikenna: 9001,
  harsh: 9002,
};

export function humanOperatorSlotId(label: string): number | null {
  return HUMAN_OPERATOR_SLOT_IDS[label.toLowerCase()] ?? null;
}

// ─── size formatting ─────────────────────────────────────────────────────

/** Human-readable byte size (1024-based), one decimal above KB. Moved here from
 * FleetKpis.tsx (ao_control_plane_read_models_and_request_path_2026_09_04.md [UI] P0) so
 * VmResources.tsx's memory-tile formatting doesn't statically pull in the whole FleetKpis
 * module (and, transitively, recharts) just for this one pure helper — that edge pinned
 * FleetKpis into the initial bundle regardless of the route-level React.lazy() split. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(1)} ${units[i]}`;
}
